/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography } from '@material-ui/core';
import { Search as SearchIcon } from '@material-ui/icons';
import { getProducts } from 'actions/Catalog';
import CardContainer from 'components/kea/CardContainer';
import FeaturePageShell from 'components/shell/FeaturePageShell';
import SafeProductImage from 'components/kea/SafeProductImage';
import { useHistory } from 'react-router-dom';
import ProductsTable from 'components/kea/tables/ProductsTable';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import SearchInput from './SearchInput';
import styles from './ProductList.module.scss';

const ProductImage = ({ src, name }) => (
  <div className={styles.thumbnail}>
    <SafeProductImage
      src={src}
      alt={name}
      className={styles.thumbnailImage}
    />
  </div>
);

const ProductList = ({ match }) => {
  const {
    catalog: { productList },
    settings,
  } = useSelector((store) => store);
  const { data, isFetching } = productList;
  const dispatch = useDispatch();
  const history = useHistory();
  const localizeMessage = useLocalizedMessage();
  const [filterQuery, setFilterQuery] = useState('');
  const [request, setRequest] = useState({
    shopId: null,
    query: '',
    page: 0,
    rowsPerPage: 25,
  });

  useEffect(() => {
    const query = filterQuery.trim();
    const timeout = window.setTimeout(() => {
      setRequest(current => (
        current.query === query
          ? current
          : { ...current, query, page: 0 }
      ));
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [filterQuery]);

  useEffect(() => {
    setFilterQuery('');
    setRequest(current => ({
      ...current,
      shopId: settings.shopId || null,
      query: '',
      page: 0,
    }));
  }, [settings.shopId]);

  useEffect(() => {
    if (settings.isFetched && settings.shopId && request.shopId === settings.shopId) {
      dispatch(getProducts(
        settings.shopId,
        request.page * request.rowsPerPage,
        request.rowsPerPage,
        request.query,
        true,
      ));
    }
  }, [dispatch, request, settings.isFetched, settings.shopId]);

  const headCells = [
    {
      id: 'image', numeric: false, label: '', sortable: false,
    },
    {
      id: 'name', numeric: false, label: localizeMessage('catalog.product.table.name'), sortable: false,
    },
    {
      id: 'vendorCode', numeric: false, label: localizeMessage('catalog.product.table.vendorCode'), sortable: false,
    },
  ];

  const visibleProducts = useMemo(() => (data || []).map((product) => ({
      ...product,
      accessibleName: product.name,
      image: <ProductImage src={product.thumbnail} name={product.name} />,
      name: (
        <span className={styles.productName}>
          {product.name}
          <span className={styles.productMeta}>{product.id}</span>
        </span>
      ),
    })), [data]);

  const filterHandler = ({ target }) => {
    setFilterQuery(target.value);
  };
  const clearFilter = () => {
    setFilterQuery('');
  };

  const filterInput = (
    <SearchInput
      onClear={clearFilter}
      value={filterQuery}
      changeHandler={filterHandler}
      isFiltering={isFetching}
    />
  );

  const rowClickHandler = (event, id) => {
    if (event.ctrlKey) {
      return window.open(`${window.location.href}/${id}`, '_blank');
    }
    history.push(`${match.url}/${id}`);
  };

  return (
    <FeaturePageShell
      title={localizeMessage('catalog.product.header')}
      description={localizeMessage('catalog.product.description')}
      width="expanded"
    >
      <CardContainer
        styleName={styles.catalogCard}
        isFetching={isFetching}
        headerId="catalog.product.browse"
        actionComponent={filterInput}
      >
        {
          data && visibleProducts.length > 0 && (
            <ProductsTable
              rowHandleClick={rowClickHandler}
              rows={visibleProducts}
              headCells={headCells}
              pagination={{
                page: request.page,
                rowsPerPage: request.rowsPerPage,
                total: Number(productList.total || 0),
                onPageChange: (page) => setRequest(current => ({ ...current, page })),
                onRowsPerPageChange: (value) => {
                  setRequest(current => ({
                    ...current,
                    rowsPerPage: value,
                    page: 0,
                  }));
                },
              }}
            />
          )
        }
        {!isFetching && data && visibleProducts.length === 0 && (
          <Box className={styles.emptyState} data-testid="product-catalog-empty-state">
            <SearchIcon className={styles.emptyIcon} />
            <Typography variant="h6">
              {localizeMessage(filterQuery
                ? 'catalog.product.empty.filtered.title'
                : 'catalog.product.empty.title')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {localizeMessage(filterQuery
                ? 'catalog.product.empty.filtered.description'
                : 'catalog.product.empty.description')}
            </Typography>
          </Box>
        )}
      </CardContainer>
    </FeaturePageShell>
  );
};

export default ProductList;
